import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Navbar } from "./components/Navbar";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import BrowseCampaigns from "./pages/BrowseCampaigns";
import CampaignDetail from "./pages/CampaignDetail";
import ReaderDashboard from "./pages/ReaderDashboard";
import AuthorDashboard from "./pages/AuthorDashboard";
import CreateCampaign from "./pages/CreateCampaign";
import EditCampaign from "./pages/EditCampaign";
import Profile from "./pages/Profile";
import AuthCallback from "./pages/AuthCallback";
import AuthorPublicPage from "./pages/AuthorPublicPage";
import Messages from "./pages/Messages";
import Contact from "./pages/Contact";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";

function Protected({ children, role, admin }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10" style={{ color: "var(--ink-muted)" }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !user.is_admin) return <Navigate to="/" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "author" ? "/author" : "/reader"} replace />;
  return children;
}

// Detect session_id fragment synchronously (before ProtectedRoute runs)
function AppRoutes() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/campaigns" element={<BrowseCampaigns />} />
      <Route path="/campaigns/:id" element={<CampaignDetail />} />
      <Route path="/authors/:id" element={<AuthorPublicPage />} />
      <Route path="/reader" element={<Protected role="reader"><ReaderDashboard /></Protected>} />
      <Route path="/author" element={<Protected role="author"><AuthorDashboard /></Protected>} />
      <Route path="/author/new" element={<Protected role="author"><CreateCampaign /></Protected>} />
      <Route path="/author/campaign/:id/edit" element={<Protected role="author"><EditCampaign /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<Protected admin><AdminDashboard /></Protected>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function Footer() {
  return (
    <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-xs flex flex-col sm:flex-row justify-between gap-2 border-t mt-10" style={{ borderColor: "var(--border-soft)", color: "var(--ink-muted)" }}>
      <span>© {new Date().getFullYear()} Faraway Sister Haven · Made for authors &amp; readers</span>
      <div className="flex items-center gap-4">
        <Link to="/contact" className="hover:opacity-80" data-testid="footer-contact-link">Contact</Link>
        <Link to="/admin/login" className="hover:opacity-80 opacity-60 hover:opacity-100" data-testid="footer-admin-link">Admin</Link>
        <span>v0.2 · Neon edition</span>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Navbar />
          <Toaster position="top-right" richColors theme="dark" />
          <main>
            <AppRoutes />
          </main>
          <Footer />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}
